
import Vue, { PropType } from 'vue';
import _trim from 'lodash/trim';
import { mapActions } from 'vuex';
import {
	FeedbackReportKind,
	FeedbackReportPriority,
	FeedbackReportStatus,
	FEEDBACK_REPORT_PRIORITIES,
	FEEDBACK_REPORT_TAGS_MAX,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { getShowTextCounterAt } from '@/utils/fieldValidationUtils';
import FeedbackShotPicker from './FeedbackShotPicker.vue';
import { cleanTag, commentLengthRule, httpLink, KIND_LABELS, KIND_QUESTIONS, PRIORITY_LABELS, STATUS_LABELS } from './feedbackReports';
import { createReport, feedbackApiFailure } from './feedbackWrites';

// What happened and what was expected, each at most (BOARD_FEEDBACK_LIMITS.maxTextLength)
const TEXT_MAX: number = 4000;
// Where a ticket filed here may start: Ready for agent approves it, as you
const STARTS: FeedbackReportStatus[] = ['new', 'triaged', 'needs_human', 'ready_for_agent'];

// Filing a ticket on the Feedback page, as the signed-in person, through restServer
// (feedback_service.create_report): its kind and words, where it starts, its priority and tags, a
// link, and up to six screenshots (chosen, pasted or dropped). It has no board. Emits `created`
// with its number.
export default Vue.extend({
	name: 'FeedbackCreateDialog',
	props: {
		open: { type: Boolean, default: false },
		// the tags in use, to pick from
		tags: { type: Array as PropType<string[]>, default: () => [] },
	},
	data () {
		return {
			MAX: TEXT_MAX,
			showCounterAt: getShowTextCounterAt(TEXT_MAX),
			formValid: true,
			saving: false,
			kind: 'bug' as FeedbackReportKind,
			status: 'new' as FeedbackReportStatus,
			happened: '',
			expected: '',
			note: '',
			priority: null as FeedbackReportPriority | null,
			chosenTags: [] as string[],
			tagSearch: null as string | null,
			link: '',
			shots: [] as File[],
			kindItems: Object.keys(KIND_LABELS).map((value: string) => ({ text: KIND_LABELS[value], value })),
			statusItems: STARTS.map((value: FeedbackReportStatus) => ({ text: STATUS_LABELS[value], value })),
			priorityItems: [
				{ text: 'No priority', value: null },
				...FEEDBACK_REPORT_PRIORITIES.map((value: FeedbackReportPriority) => ({ text: PRIORITY_LABELS[value], value })),
			],
		};
	},
	computed: {
		questions (): { happened: string, expected: string } {
			return KIND_QUESTIONS[this.kind];
		},
		lengthRules (): Array<(value: string) => boolean | string> {
			return [(value: string) => _trim(value || '').length <= TEXT_MAX || `Must be ${TEXT_MAX} characters or fewer`];
		},
		happenedRules (): Array<(value: string) => boolean | string> {
			return [(value: string) => !!_trim(value || '') || 'Required', ...this.lengthRules];
		},
		noteRules (): Array<(value: string) => boolean | string> {
			return [(value: string) => !!_trim(value || '') || 'Required', commentLengthRule];
		},
		cleanedTags (): Array<string | null> {
			return this.chosenTags.map((tag: string) => cleanTag(tag));
		},
		tagsRule (): () => boolean | string {
			return (): boolean | string => {
				if (this.cleanedTags.some((tag: string | null) => !tag)) return 'Letters, digits, spaces and . _ : / + # & \' - only, up to 40 each';
				if (this.chosenTags.length > FEEDBACK_REPORT_TAGS_MAX) return `At most ${FEEDBACK_REPORT_TAGS_MAX} tags`;
				return true;
			};
		},
		linkRule (): (value: string) => boolean | string {
			return (value: string): boolean | string => !_trim(value || '') || (!!httpLink(value) && value.trim().length <= 500) || 'A link that starts with https://';
		},
		canFile (): boolean {
			const happened: string = _trim(this.happened || '');
			if (!happened || happened.length > TEXT_MAX || _trim(this.expected || '').length > TEXT_MAX) return false;
			if (this.status === 'needs_human' && !_trim(this.note || '')) return false;
			if (this.tagsRule() !== true || this.linkRule(this.link) !== true) return false;
			return this.formValid;
		},
	},
	watch: {
		open (open: boolean) {
			if (!open) return;
			this.kind = 'bug';
			this.status = 'new';
			this.happened = '';
			this.expected = '';
			this.note = '';
			this.priority = null;
			this.chosenTags = [];
			this.link = '';
			this.shots = [];
			// after the dialog has opened and taken focus itself
			window.setTimeout(() => {
				const field = this.$refs.happened as Vue | undefined;
				const input: HTMLElement | null = field ? field.$el.querySelector('textarea') : null;
				if (input) input.focus();
			}, 250);
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		onInput (open: boolean) {
			if (!open) this.cancel();
		},
		cancel () {
			if (!this.saving) this.$emit('cancel');
		},
		// A picture pasted into what happened goes with the ticket; text pastes as text
		onPaste (e: ClipboardEvent) {
			const picker = this.$refs.picker as (Vue & { addFromPaste: (event: ClipboardEvent) => boolean }) | undefined;
			if (picker && picker.addFromPaste(e)) e.preventDefault();
		},
		async file () {
			if (!this.canFile || this.saving) return;
			this.saving = true;
			try {
				const result = await createReport({
					kind: this.kind,
					happened: _trim(this.happened),
					expected: _trim(this.expected || ''),
					status: this.status,
					note: this.status === 'needs_human' ? _trim(this.note || '') : '',
					priority: this.priority,
					tags: this.cleanedTags as string[],
					link: _trim(this.link || ''),
				}, this.shots);
				const failure = feedbackApiFailure(result);
				if (failure) {
					console.error('[feedback] failed to file a ticket', failure.code);
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: failure.message, show: true });
					return;
				}
				const number: number = result.ok === true ? result.value.number : 0;
				this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message: `Filed #${number}`, show: true });
				this.$emit('created', number);
			} finally {
				this.saving = false;
			}
		},
	},
	components: {
		ActionsIsland,
		DialogContent,
		DialogOrBottomSheet,
		FeedbackShotPicker,
	},
});
